/**
 * Página de recompensas (Modelo B): muestra el saldo gastable (XP ganado − gastado,
 * no afecta al ranking) y el catálogo. Cada recompensa abre su página de detalle.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CircleStar, ArrowRight } from 'lucide-react';
import { Spinner } from '@/components/atoms/Spinner';
import { Badge } from '@/components/atoms/Badge';
import { ErrorState } from '@/components/molecules/ErrorState';
import { buildRoute, ROUTES } from '@/constants/routes';
import { getRewards, type RewardsResponse } from '@/services/api/rewards.api';
import { rewardTitle, rewardDesc } from './rewardMeta';
import styles from './RewardsInvitePages.module.scss';

export function RewardsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<RewardsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      setData(await getRewards());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>{t('rewardsPage.title')}</h1>
        <p className={styles.page__subtitle}>{t('rewardsPage.subtitle')}</p>
      </header>

      {loading && !data ? (
        <div className={styles.page__placeholder}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      ) : error ? (
        <ErrorState onRetry={() => void load()} />
      ) : data ? (
        <>
          <div className={styles.rewards__wallet}>
            <span className={styles.rewards__walletValue}>{data.balance}</span>
            <span className={styles.rewards__walletLabel}>{t('rewardsPage.walletLabel')}</span>
          </div>

          <div className={styles.rewards__list}>
            {data.items.map((item) => {
              const status = item.owned ? 'owned' : item.redeemable ? 'available' : 'notEnough';
              const statusVariant =
                status === 'available' ? 'success' : status === 'owned' ? 'info' : 'warning';
              return (
                <button
                  key={item.id}
                  type="button"
                  className={styles.rewards__cardLink}
                  data-no-press-scale=""
                  onClick={() =>
                    navigate(buildRoute(ROUTES.STUDENT.REWARD_DETAIL, { rewardId: item.id }))
                  }
                >
                  <span className={styles.rewards__cardBody}>
                    <span className={styles.rewards__cardTitle}>{rewardTitle(item, t, i18n.language)}</span>
                    <span className={styles.rewards__cardDesc}>{rewardDesc(item, t, i18n.language)}</span>
                    <span className={styles.rewards__cardCostRow}>
                      <span className={styles.rewards__cardCost}>
                        <CircleStar size={14} aria-hidden /> {t('rewardsPage.cost', { cost: item.cost })}
                      </span>
                      <Badge variant={item.comingSoon ? 'secondary' : statusVariant} size="sm">
                        {item.comingSoon ? t('rewardsPage.comingSoon') : t(`rewardsPage.${status}`)}
                      </Badge>
                    </span>
                  </span>
                  <span className={styles.rewards__cardArrow} aria-hidden>
                    <ArrowRight size={20} />
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

RewardsPage.displayName = 'RewardsPage';
export default RewardsPage;
